/**
 * The release-notes page, as both surfaces that embed it need to address it: the modal
 * that announces a new release (`ProductNewsModal`) and the navbar drawer
 * (`ProductNewsDrawer`), both through `ProductNewsFrame`.
 *
 * The page is on another origin, so it cannot be measured — it reports its own height
 * instead, and says nothing when it has nothing to show.
 */
import config from 'App/babel/config';
import { getCurrentLanguage } from 'Components/Helpers/translate';
import logError from 'Helpers/logError';

/** What the page posts once it has rendered, carrying its height in pixels. */
const HEIGHT_MESSAGE = 'upsales-product-news-height';
/** Sanity bound on a height measured by a document we do not control. */
const MAX_HEIGHT = 4000;
/** After the page has loaded, how long its height may still be on the way. */
const SETTLE_MS = 2000;
/** How long a page that never loads at all is given. */
const LOAD_MS = 20000;

/**
 * The pages host also serves customer-authored html, so the frame is denied everything it
 * does not need. No `allow-same-origin`: the frame runs at an opaque origin, so a script
 * that reaches that host cannot touch the host's cookies or storage from inside our
 * chrome. Its height then arrives from origin "null", which is why the message check
 * below authenticates on the source window rather than the origin.
 */
export const FRAME_SANDBOX = 'allow-scripts allow-popups';

/**
 * The configured page, parsed once and rejected unless it is somewhere we are willing to
 * embed under a script-enabled sandbox.
 *
 * Cross-origin is a requirement, not an observation: a relative or same-host value would
 * put a scripted frame on our own origin, where it reaches `window.parent` directly.
 * https for the same reason, since the digest renders inside the app.
 */
const parsePage = (): URL | null => {
	if (!config.PRODUCT_NEWS_URL) {
		return null;
	}
	let url: URL;
	try {
		url = new URL(config.PRODUCT_NEWS_URL, window.location.href);
	} catch (e) {
		logError(e, 'Product news: PRODUCT_NEWS_URL is not a valid url', config.PRODUCT_NEWS_URL);
		return null;
	}
	const isLocal = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
	if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLocal)) {
		logError(new Error('Product news: PRODUCT_NEWS_URL must be https'), 'Product news', config.PRODUCT_NEWS_URL);
		return null;
	}
	if (url.origin === window.location.origin) {
		logError(
			new Error('Product news: PRODUCT_NEWS_URL must be on another origin'),
			'Product news',
			config.PRODUCT_NEWS_URL
		);
		return null;
	}
	return url;
};

let parsed: URL | null | undefined;
/** Config is fixed for the session, so this is parsed — and any complaint logged — once. */
const page = (): URL | null => {
	if (parsed === undefined) {
		parsed = parsePage();
	}
	return parsed;
};

/** Tests only: config is a module constant everywhere else. */
export const resetProductNewsPageCache = () => {
	parsed = undefined;
};

/**
 * Whether this customer gets the release notes at all: whether there is anywhere to point
 * at. The configured page is empty until an environment has a published release-notes
 * page, and blank in every committed config but production.
 */
export const isProductNewsEnabled = () => page() !== null;

/** The page itself, for a link out to the full reader. */
export const productNewsPageUrl = () => page()?.href ?? null;

const productNewsOrigin = () => page()?.origin ?? null;

/**
 * `compact=1` asks the page for its digest — a headline and intro per release, each
 * linking to the full page — rather than the reader, which does not fit a drawer or a
 * modal this narrow. The language travels in the url because the page only appends
 * `?lang=` for its own modules.
 */
export const productNewsFrameUrl = () => {
	const url = page();
	if (!url) {
		return null;
	}
	const framed = new URL(url.href);
	framed.searchParams.set('compact', '1');
	framed.searchParams.set('lang', getCurrentLanguage()?.toLowerCase().startsWith('sv') ? 'sv' : 'en');
	return framed.href;
};

/**
 * Follow one embedded frame: `onHeight` for every height it reports, `onUnavailable` when
 * it has none to give. Returns a teardown.
 *
 * A page that loads and still reports no height has nothing to show, and neither does one
 * that never loads. The settle check is tied to the load so a slow page is waited for
 * rather than written off, and a frame that has since left the document is nobody's
 * failure — its surface was closed.
 */
export const watchProductNewsFrame = (
	frame: HTMLIFrameElement,
	{ onHeight, onUnavailable }: { onHeight: (height: number) => void; onUnavailable: (reason: string) => void }
) => {
	const origin = productNewsOrigin();
	let sized = false;

	const onMessage = (event: MessageEvent) => {
		// The source window is the real check: it is this frame or it is not. The origin
		// is "null" under the sandbox above, and compared as well only so that a frame
		// granted its own origin is still accepted rather than silently ignored.
		if (event.source !== frame.contentWindow || (event.origin !== 'null' && event.origin !== origin)) {
			return;
		}
		const data = event.data as { type?: string; height?: unknown };
		const reported = Number(data?.height);
		if (data?.type !== HEIGHT_MESSAGE || !(reported > 0)) {
			return;
		}
		sized = true;
		onHeight(Math.min(Math.round(reported), MAX_HEIGHT));
	};

	const timers: number[] = [];
	const check = (reason: string) => () => {
		if (frame.isConnected && !sized) {
			onUnavailable(reason);
		}
	};
	const onLoad = () => timers.push(window.setTimeout(check('the page reported no height'), SETTLE_MS));

	window.addEventListener('message', onMessage);
	frame.addEventListener('load', onLoad);
	timers.push(window.setTimeout(check('the page did not load'), LOAD_MS));

	return () => {
		window.removeEventListener('message', onMessage);
		frame.removeEventListener('load', onLoad);
		timers.forEach(window.clearTimeout);
	};
};
